import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui';
import { cn } from '../../lib/utils';

interface PublicWebhookConfig {
  defaultTtlHours: number;
  confirmTtlHours: number;
  approvalTtlHours: number;
  signatureTtlHours: number;
}

const defaultConfig: PublicWebhookConfig = {
  defaultTtlHours: 168,
  confirmTtlHours: 1,
  approvalTtlHours: 168,
  signatureTtlHours: 168,
};

interface PublicWebhookListItem {
  token: string;
  expiresAt: string | null;
  payload: {
    action?: string;
    bookingId?: string;
    bookingReference?: string;
    targetStatus?: string;
    approvalStatus?: string;
    jobId?: string | null;
    createdAt?: number;
  } | null;
}

interface WebhookListResponse {
  items: PublicWebhookListItem[];
  cursor?: string;
  listComplete?: boolean;
}

export function WebhooksSettingsPage() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PublicWebhookConfig>(defaultConfig);
  const [isDirty, setIsDirty] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'settings', 'publicWebhookConfig'],
    queryFn: async () => api.get<{ key: string; value: PublicWebhookConfig }>(
      '/api/admin/settings/publicWebhookConfig'
    ),
  });
  useEffect(() => {
    if (data?.value) {
      setDraft(data.value);
      setIsDirty(false);
    }
  }, [data]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async () => api.put(
      '/api/admin/settings/publicWebhookConfig',
      { value: draft }
    ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'publicWebhookConfig'] });
      setIsDirty(false);
    },
  });

  const previewBase = useMemo(() => '/api/public/webhook', []);

  const { data: listData, isLoading: isListLoading } = useQuery({
    queryKey: ['admin', 'webhooks', 'list'],
    queryFn: async () => api.get<WebhookListResponse>('/api/admin/webhooks'),
  });
  const [cursor, setCursor] = useState<string | null>(null);
  const [items, setItems] = useState<PublicWebhookListItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    if (listData) {
      setItems(listData.items || []);
      setCursor(listData.cursor || null);
    }
  }, [listData]);

  const handleLoadMore = async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const response = await api.get<WebhookListResponse>(
        `/api/admin/webhooks?cursor=${encodeURIComponent(cursor)}`
      );
      setItems((prev) => [...prev, ...(response.items || [])]);
      setCursor(response.cursor || null);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const filteredItems = useMemo(() => {
    let next = items;
    if (actionFilter !== 'all') {
      next = next.filter((item) => item.payload?.action === actionFilter);
    }
    if (search.trim()) {
      const query = search.trim().toLowerCase();
      next = next.filter((item) => {
        const tokenMatch = item.token.toLowerCase().includes(query);
        const refMatch = item.payload?.bookingReference?.toLowerCase().includes(query);
        const bookingMatch = item.payload?.bookingId?.toLowerCase().includes(query);
        return tokenMatch || refMatch || bookingMatch;
      });
    }
    return next;
  }, [items, actionFilter, search]);

  const updateField = <K extends keyof PublicWebhookConfig>(
    key: K,
    value: PublicWebhookConfig[K]
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Webhooks Publicos</h2>
        <p className="text-xs text-gray-500 mt-1">
          Configura los enlaces de confirmacion, aprobacion y firma usados en emails.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="text-xs font-medium text-gray-600">
            TTL confirmacion (horas)
            <input
              type="number"
              min={1}
              max={720}
              value={draft.confirmTtlHours}
              onChange={(e) => updateField('confirmTtlHours', Number(e.target.value))}
              className={cn(
                'mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            />
          </label>

          <label className="text-xs font-medium text-gray-600">
            TTL aprobacion (horas)
            <input
              type="number"
              min={1}
              max={720}
              value={draft.approvalTtlHours}
              onChange={(e) => updateField('approvalTtlHours', Number(e.target.value))}
              className={cn(
                'mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            />
          </label>

          <label className="text-xs font-medium text-gray-600">
            TTL firma (horas)
            <input
              type="number"
              min={1}
              max={720}
              value={draft.signatureTtlHours}
              onChange={(e) => updateField('signatureTtlHours', Number(e.target.value))}
              className={cn(
                'mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            />
          </label>

          <label className="text-xs font-medium text-gray-600">
            TTL por defecto (horas)
            <input
              type="number"
              min={1}
              max={720}
              value={draft.defaultTtlHours}
              onChange={(e) => updateField('defaultTtlHours', Number(e.target.value))}
              className={cn(
                'mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            />
          </label>
        </div>

        <div>
          <p className="text-[11px] text-gray-500">
            Ruta fija: <span className="font-mono">{previewBase}/&#123;token&#125;</span>
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => mutateAsync()}
          disabled={isLoading || isPending || !isDirty}
        >
          <Save className="w-4 h-4 mr-2" />
          Guardar cambios
        </Button>
        {isDirty && (
          <span className="text-xs text-gray-500">Cambios sin guardar</span>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Webhooks activos</h3>
            <p className="text-xs text-gray-500">Tokens vigentes en KV.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-4">
          <div>
            <label className="text-[11px] font-medium text-gray-500">Buscar</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Token, referencia, booking..."
              className={cn(
                'mt-1 w-56 rounded-md border border-gray-200 px-3 py-2 text-xs',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-gray-500">Accion</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className={cn(
                'mt-1 w-44 rounded-md border border-gray-200 px-3 py-2 text-xs bg-white',
                'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
              )}
            >
              <option value="all">Todas</option>
              <option value="booking_confirmation">Confirmacion</option>
              <option value="job_transition">Transicion</option>
              <option value="signature">Firma</option>
            </select>
          </div>
        </div>

        {isListLoading ? (
          <p className="text-xs text-gray-500">Cargando...</p>
        ) : filteredItems.length === 0 ? (
          <p className="text-xs text-gray-500">No hay webhooks activos.</p>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <div key={item.token} className="border border-gray-100 rounded-md p-3">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono text-gray-700">{item.token}</span>
                  <span className="text-gray-500">{item.payload?.action || 'unknown'}</span>
                  {item.payload?.bookingReference && (
                    <span className="text-gray-500">Ref {item.payload.bookingReference}</span>
                  )}
                  {item.payload?.targetStatus && (
                    <span className="text-gray-500">→ {item.payload.targetStatus}</span>
                  )}
                  {item.expiresAt && (
                    <span className="text-gray-400">Expira {new Date(item.expiresAt).toLocaleString('es-ES')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {cursor && (
          <div className="mt-4">
            <Button variant="secondary" size="sm" onClick={handleLoadMore} disabled={isLoadingMore}>
              {isLoadingMore ? 'Cargando...' : 'Cargar mas'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
